import React from 'react'
import { Trans, useTranslation } from 'react-i18next'

import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import InputLabel from '@mui/material/InputLabel'

import FileUpload from 'cityads-v3/components/FileUpload/FileUpload'
import TextField from 'cityads-v3/components/TextField/TextField'
import { LegalSubjectId } from 'cityads-v3/reducers/advertisingLawForm'
import { MAX_FILE_SIZE } from 'cityads-v3/utils/apiUtils'

import { FormFields } from './AdvertisingMarkingForm.hooks'

import classes from './AdvertisingMarkingForm.scss'

type Step3Props = {
  legalSubject   : FormFields['legalSubject']
  legalName      : FormFields['legalName']
  vatNumber      : FormFields['vatNumber']
  psrn           : FormFields['psrn']
  legalAddress   : FormFields['legalAddress']
  email          : FormFields['email']
  websiteUrl     : FormFields['websiteUrl']
  ownershipRights: FormFields['ownershipRights']
}

const PSRN_TRANSLATION_KEYS = {
  [LegalSubjectId.legalEntity]: 'advertisingMarking.form.psrn',
  [LegalSubjectId.soleProprietor]: 'advertisingMarking.form.psrn_sp',
}

export default function Step3({ legalSubject, legalName, vatNumber, psrn, legalAddress, email, websiteUrl, ownershipRights }: Step3Props): JSX.Element {
  const { t } = useTranslation(['Publisher'])

  return (
    <>
      {legalSubject.value === LegalSubjectId.legalEntity ?
        (
          <TextField
            label      = {t('advertisingMarking.form.legal_name')}
            helperText = {legalName.error}
            error      = {!!legalName.error}
            value      = {legalName.value || ''}
            onChange   = {legalName.handleChange}
            margin     = "normal"
            autoFocus
            fullWidth
            required
          />
        )
        :
        null
      }
      <TextField
        label      = {t('advertisingMarking.form.vat_number')}
        helperText = {vatNumber.error || (
          <Trans t={t} i18nKey="advertisingMarking.form.vat_number_helper">
            You can inquire your NIP number on
            <a href="https://www.nalog.gov.ru/" target="_blank" rel="noopener noreferrer">the website of the Federal Tax Service of Russia</a>.
          </Trans>
        )}
        error      = {!!vatNumber.error}
        value      = {vatNumber.value || ''}
        onChange   = {vatNumber.handleChange}
        margin     = "normal"
        fullWidth
        required
      />
      {legalSubject.value === LegalSubjectId.selfEmployed ?
        null
        :
        (
          <TextField
            label      = {t(PSRN_TRANSLATION_KEYS[legalSubject.value])}
            helperText = {psrn.error}
            error      = {!!psrn.error}
            value      = {psrn.value || ''}
            onChange   = {psrn.handleChange}
            margin     = "normal"
            fullWidth
            required
          />
        )
      }
      {legalSubject.value === LegalSubjectId.legalEntity ?
        (
          <TextField
            label      = {t('advertisingMarking.form.legal_address')}
            helperText = {legalAddress.error}
            error      = {!!legalAddress.error}
            value      = {legalAddress.value || ''}
            onChange   = {legalAddress.handleChange}
            margin     = "normal"
            fullWidth
            required
          />
        )
        :
        null
      }
      <TextField
        label      = {t('advertisingMarking.form.email')}
        helperText = {email.error}
        error      = {!!email.error}
        value      = {email.value || ''}
        onChange   = {email.handleChange}
        margin     = "normal"
        fullWidth
        required
      />
      <TextField
        label      = {t('advertisingMarking.form.website_url')}
        helperText = {websiteUrl.error}
        error      = {!!websiteUrl.error}
        value      = {websiteUrl.value || ''}
        onChange   = {websiteUrl.handleChange}
        margin     = "normal"
        fullWidth
        required
      />
      <FormControl variant="standard" margin="normal" error={!!ownershipRights.error} fullWidth>
        <InputLabel required shrink className={classes.fileFormLabel}>{t('advertisingMarking.form.ownership_rights')}</InputLabel>
        <div className={classes.fileFormControl}>
          <FileUpload
            value    = {ownershipRights.fileValue}
            onChange = {ownershipRights.handleFileChange}
            maxSize  = {MAX_FILE_SIZE}
          />
        </div>
        <FormHelperText>{ownershipRights.error}</FormHelperText>
      </FormControl>
    </>
  )
}
